/**
 * EndScreen: the booking's end screen (booked, request sent, request cancelled), shared by the Mini
 * App's BookedEndPage and the studio website's phone booked and request-sent pages
 * (shared-client-screens D3, rows K1 to K11). Props only: the pinned photo, the rising sheet with the
 * status head and the rows (bookingRules' endRows), each row's own action on its right (Add to
 * Calendar beside When, the surface's calendar control; Get directions beside the place), and the
 * pinned buttons: Done, and on a request Cancel request, confirmed in the app's sheet (rows K8, K9).
 * Each surface wires Done (it replaces history and vibrates, row K4b) and where it goes.
 */
import { useState, type ReactNode } from 'react';
import { BookingHero, type BookingHeroProps } from '@/components/booking/BookingHero';
import { CalendarMenu, type CalendarChoices, type CalendarMenuParts } from '@/components/booking/CalendarMenu';
import { ClientSheet, clientSheetStyles as sheetStyles } from '@/components/client/ClientSheet';
import type { EndRow } from '@/pages/booking/shared/bookingRules';
import { CalendarIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CloseIcon, CreditCardIcon, GlobeIcon, MapPinIcon, PhoneIcon } from '@/pages/ConfirmPage/confirmIcons';
import styles from '@/pages/ConfirmPage/ConfirmPage.module.css';

const ROW_ICONS: Record<EndRow['icon'], typeof CalendarIcon> = {
  when: CalendarIcon,
  duration: ClockIcon,
  place: MapPinIcon,
  online: GlobeIcon,
  phone: PhoneIcon,
  payment: CreditCardIcon,
};

const STATUS_ICONS = { check: CheckIcon, clock: ClockIcon, close: CloseIcon } as const;

/** The end screen's placeholder while the booking is read (row K10). */
export function EndScreenLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    </div>
  );
}

/** A request the client can still withdraw: the sheet's words and its confirm. */
export interface EndScreenCancel {
  /** "Tuesday, November 3": the requested day, as the sheet names it. */
  readonly dateLabel: string | null;
  readonly providerName: string | null;
  /** True while the request is being cancelled: the Cancelling… card (row K9). */
  readonly pending: boolean;
  readonly onConfirm: () => void;
}

export interface EndScreenProps {
  hero: BookingHeroProps;
  /** A cancelled request: the photo and rows muted, the status icon muted. */
  cancelled?: boolean;
  status: { readonly icon: keyof typeof STATUS_ICONS; readonly title: string; readonly lines: readonly ReactNode[] };
  rows: readonly EndRow[];
  /** Add to Calendar beside When (CalendarMenu): the choices and how each side opens them; none without one. */
  calendar?: {
    readonly choices: CalendarChoices;
    readonly onApple: () => void;
    /** The app's Telegram link opener; left out on the website (plain links to a new tab). */
    readonly onOpen?: (url: string) => void;
    readonly parts?: CalendarMenuParts;
  } | null;
  /** Get directions beside the place: the app opens it outside Telegram, the website in a new tab. */
  onDirections: (href: string) => void;
  /** The bottom action: Done. */
  done: { readonly label: string; readonly onPress: () => void };
  /** On a request still waiting: Cancel request under Done. */
  cancel?: EndScreenCancel | null;
  testId?: string;
}

export function EndScreen({ hero, cancelled = false, status, rows, calendar, onDirections, done, cancel, testId }: EndScreenProps) {
  const [asking, setAsking] = useState(false);
  const StatusIcon = STATUS_ICONS[status.icon];
  return (
    <div className={styles.page} data-testid={testId}>
      <BookingHero {...hero} muted={cancelled} />

      <div className={styles.scroll}>
        <div className={styles.heroSpacer} aria-hidden="true" />
        <div className={styles.sheet}>
          <div className={styles.grab} aria-hidden="true" />
          <div className={styles.body}>
            <div className={styles.statusHead}>
              <span className={`${styles.statusIcon} ${cancelled ? styles.statusIconMuted : ''}`}>
                <StatusIcon />
              </span>
              <div>
                <h1 className={styles.statusTitle}>{status.title}</h1>
                {status.lines.map((line, i) => (
                  <p key={i} className={styles.statusSubtitle}>
                    {line}
                  </p>
                ))}
              </div>
            </div>

            <div className={`${styles.det} ${cancelled ? styles.detDim : ''}`}>
              {rows.map((row) => {
                const Icon = ROW_ICONS[row.icon];
                return (
                  <div key={row.label} className={styles.row}>
                    <Icon />
                    <div>
                      <div className={styles.rowLabel}>{row.label}</div>
                      <div className={styles.rowValue}>{row.value}</div>
                      {row.line && <div className={styles.rowValue}>{row.line}</div>}
                    </div>
                    {row.amount && <span className={styles.rowAmount}>{row.amount}</span>}
                    {/* Each row's own action on its right (Gabriel 2026-10-22). No Join button here. */}
                    {row.icon === 'when' && calendar && (
                      <CalendarMenu choices={calendar.choices} onApple={calendar.onApple} onOpen={calendar.onOpen} parts={calendar.parts} className={styles.rowLink}>
                        <CalendarPlusIcon />
                        Add to Calendar
                      </CalendarMenu>
                    )}
                    {row.directionsHref && (
                      <button type="button" className={styles.rowLink} onClick={() => onDirections(row.directionsHref!)}>
                        <MapPinIcon />
                        Get directions
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.foot}>
        <div className={styles.actions}>
          {/* Add to Calendar rides the When row (Gabriel 2026-10-22); Done stays the bottom action. */}
          <button type="button" className={styles.btnPrimary} onClick={done.onPress}>
            {done.label}
            <CheckIcon />
          </button>
          {cancel && (
            <button type="button" className={`${styles.btnGhost} ${styles.btnGhostDanger}`} disabled={cancel.pending} onClick={() => setAsking(true)}>
              Cancel request
            </button>
          )}
        </div>
      </div>

      {cancel && !cancel.pending && (
        <ClientSheet open={asking} onClose={() => setAsking(false)} draggable kicker="Cancel request" title="Cancel this request?" testId="cancel-request-sheet">
          <div className={sheetStyles.copy}>
            Your request for <b>{cancel.dateLabel ?? 'this time'}</b>
            {cancel.providerName ? <> with {cancel.providerName}</> : null} will be cancelled. This can&apos;t be undone.
          </div>
          <div className={sheetStyles.nudge}>
            Need a different time instead? Cancel, then pick a new slot from the service page. It takes under a minute.
          </div>
          <div className={sheetStyles.actions}>
            <button type="button" className={sheetStyles.keep} onClick={() => setAsking(false)}>
              Keep my request
            </button>
            <button
              type="button"
              className={sheetStyles.danger}
              onClick={() => {
                setAsking(false);
                cancel.onConfirm();
              }}
            >
              Yes, cancel request
            </button>
          </div>
        </ClientSheet>
      )}

      {cancel?.pending && (
        <div className={styles.cancelFeedbackBackdrop} data-testid="cancel-progress">
          <div className={styles.cancelProgress} role="status" aria-live="polite">
            <span className={styles.cancelSpinner} aria-hidden="true" />
            <span>Cancelling…</span>
          </div>
        </div>
      )}
    </div>
  );
}
